import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import type { ProfileUpdate } from '@/db/profile';
import { getProfile, upsertProfile } from '@/db/profile';
import { getDb } from '@/lib/db';
import { authMiddleware } from '@/lib/server-fn-auth';

/** Validation schema for profile updates (single source of truth for the PUT shape). */
export const profilePutSchema = z
  .object({
    application_answers: z.string().nullable().optional(),
    disable_weekend_scans: z.number().int().min(0).max(1).nullable().optional(),
    disabled_scrapers: z.string().nullable().optional(),
    discord_webhook: z.string().max(500).nullable().optional(),
    experience_years: z.number().int().nullable().optional(),
    linkedin_cookie: z.string().nullable().optional(),
    location_preference: z.string().max(200).nullable().optional(),
    match_threshold: z.number().int().min(0).max(100).nullable().optional(),
    max_score_per_cycle: z.number().int().min(0).nullable().optional(),
    max_tailor_per_cycle: z.number().int().min(0).nullable().optional(),
    min_salary: z.number().int().nullable().optional(),
    name: z.string().min(1).max(200).optional(),
    offpeak_interval_min: z.number().int().min(1).nullable().optional(),
    peak_end_hour: z.number().int().min(0).max(23).nullable().optional(),
    peak_interval_min: z.number().int().min(1).nullable().optional(),
    peak_start_hour: z.number().int().min(0).max(23).nullable().optional(),
    preferences: z.string().nullable().optional(),
    requirements: z.string().nullable().optional(),
    resume_file_name: z.string().max(255).nullable().optional(),
    resume_file_path: z.string().max(1024).nullable().optional(),
    resume_md: z.string().nullable().optional(),
    resume_raw: z.string().nullable().optional(),
    scoring_model: z.string().max(100).nullable().optional(),
    skills: z.string().optional(),
    summary: z.string().nullable().optional(),
    tailoring_model: z.string().max(100).nullable().optional(),
    target_roles: z.string().optional(),
    timezone: z.string().max(64).nullable().optional(),
  })
  .strict();

/** Current profile singleton. Mirrors `GET /api/profile`. */
export const fetchProfile = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .handler(async () => {
    const db = getDb();

    return { profile: await getProfile(db) };
  });

/** Upsert the profile singleton. Mirrors `PUT /api/profile`. */
export const updateProfile = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(profilePutSchema)
  .handler(async ({ data }) => {
    const db = getDb();

    return { profile: await upsertProfile(db, data as ProfileUpdate) };
  });
